import {
  Alert,
  Box,
  Button,
  CardMedia,
  CircularProgress,
  Container,
  Divider,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import React, { useState } from "react";
import { resetPassword } from "../firebase";
import logo from "../imgs/logo192.png";

const FIREBASE_ERROR_MESSAGES = {
  "auth/user-not-found": "No existe ninguna cuenta con ese correo electrónico.",
  "auth/invalid-email": "El formato del correo electrónico no es válido.",
  "auth/network-request-failed": "Error de red. Verifica tu conexión a internet.",
  "auth/too-many-requests": "Demasiados intentos. Espera un momento antes de volver a intentarlo.",
};

const getErrorMessage = (error) => {
  const code = error?.code || "";
  return FIREBASE_ERROR_MESSAGES[code] || "Ocurrió un error inesperado. Inténtalo de nuevo.";
};

const cardSx = {
  background: "rgba(255,255,255,0.07)",
  backdropFilter: "blur(12px)",
  borderRadius: 3,
  border: "1px solid rgba(255,255,255,0.12)",
  p: { xs: 3, sm: 4 },
  boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
};

const inputSx = {
  mt: 2,
  "& .MuiInputBase-root": { color: "white" },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "rgba(255,255,255,0.35)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.7)" },
    "&.Mui-focused fieldset": { borderColor: "white" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "white" },
};

const gradientBg = {
  minWidth: "100vw",
  minHeight: "100vh",
  background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Por favor ingresa tu correo electrónico.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await resetPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <Box sx={gradientBg}>
        <Container maxWidth="xs">
          <Box sx={cardSx} textAlign="center">
            <MarkEmailReadOutlinedIcon
              sx={{ fontSize: 64, color: "#ce93d8", mb: 2 }}
            />
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "white", mb: 1 }}
            >
              Revisa tu correo
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.6)", mb: 1 }}
            >
              Enviamos un enlace de restablecimiento a:
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "white",
                fontWeight: 600,
                background: "rgba(255,255,255,0.08)",
                borderRadius: 2,
                px: 2,
                py: 1,
                display: "inline-block",
                mb: 3,
                wordBreak: "break-all",
              }}
            >
              {email}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.5)", mb: 3, fontSize: "0.82rem" }}
            >
              Si no lo ves, revisa la carpeta de spam o correo no deseado.
            </Typography>

            <Button
              variant="outlined"
              fullWidth
              disabled={loading}
              onClick={() => {
                setLoading(true);
                resetPassword(email.trim()).finally(() => setLoading(false));
              }}
              sx={{
                mb: 1.5,
                borderColor: "rgba(255,255,255,0.3)",
                color: "rgba(255,255,255,0.7)",
                "&:hover": {
                  borderColor: "white",
                  color: "white",
                  background: "rgba(255,255,255,0.05)",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : (
                "Reenviar correo"
              )}
            </Button>

            <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.12)" }} />

            <Link
              href="/login"
              underline="hover"
              sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.9rem" }}
            >
              ← Volver al inicio de sesión
            </Link>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={gradientBg}>
      <Container maxWidth="xs">
        <Box sx={cardSx}>
          <CardMedia
            component="img"
            image={logo}
            title="Chatio"
            sx={{
              width: 72,
              height: 72,
              mx: "auto",
              mb: 2,
              borderRadius: "50%",
              boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
            }}
          />
          <Typography
            variant="h5"
            component="h1"
            textAlign="center"
            sx={{ fontWeight: 600, color: "white", mb: 0.5 }}
          >
            ¿Olvidaste tu contraseña?
          </Typography>
          <Typography
            variant="body2"
            textAlign="center"
            sx={{ color: "rgba(255,255,255,0.55)", mb: 2 }}
          >
            Ingresa tu correo y te enviaremos un enlace para restablecerla.
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                background: "rgba(211,47,47,0.15)",
                color: "#ff8a80",
                border: "1px solid rgba(211,47,47,0.3)",
                "& .MuiAlert-icon": { color: "#ff8a80" },
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              label="Correo electrónico"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={inputSx}
              fullWidth
              disabled={loading}
            />

            <Button
              variant="contained"
              type="submit"
              disabled={loading}
              fullWidth
              sx={{
                mt: 3,
                py: 1.3,
                fontWeight: 600,
                fontSize: "0.95rem",
                background: "linear-gradient(90deg, #e040fb, #7c4dff)",
                borderRadius: 2,
                boxShadow: "0 4px 16px rgba(124,77,255,0.35)",
                "&:hover": {
                  background: "linear-gradient(90deg, #ce39e0, #651fff)",
                  boxShadow: "0 4px 20px rgba(124,77,255,0.55)",
                },
                "&.Mui-disabled": { opacity: 0.6 },
              }}
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: "white" }} />
              ) : (
                "Enviar enlace"
              )}
            </Button>
          </Box>

          <Divider sx={{ my: 3, borderColor: "rgba(255,255,255,0.12)" }} />

          <Typography
            textAlign="center"
            sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.9rem" }}
          >
            <Link
              href="/login"
              underline="hover"
              sx={{ color: "white", fontWeight: 600 }}
            >
              ← Volver al inicio de sesión
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
